/* eslint-disable */
import api, { ensureFreshToken } from './httpClient';

// Вспомогательные функции
const validateId = (id) => {
    if (!id && id !== 0) {
        throw new Error('ID is required');
    }
    return id;
};

const validateUserId = (userId) => {
    if (!userId && userId !== 0) {
        throw new Error('User ID is required');
    }
    return userId;
};

const extractApiErrorMessage = (error, fallbackMessage = 'Произошла ошибка. Попробуйте снова.') => {
    const response = error?.response;
    if (response?.data) {
        const data = response.data;

        if (typeof data === 'string') {
            return data;
        }

        if (Array.isArray(data) && data.length > 0) {
            return data[0];
        }

        if (typeof data === 'object') {
            if (data.non_field_errors && data.non_field_errors.length > 0) {
                return data.non_field_errors[0];
            }
            if (data.detail) {
                return data.detail;
            }
            // E03 — вьюсеты платформы часто отвечают `{"error": "строка"}`
            // (гард категорий события, загрузка обложки и т.д.). Раньше эта
            // форма не читалась, и в UI уезжал технический фолбэк.
            if (typeof data.error === 'string' && data.error) {
                return data.error;
            }
            const firstKey = Object.keys(data)[0];
            if (firstKey && Array.isArray(data[firstKey]) && data[firstKey].length > 0) {
                return data[firstKey][0];
            }
        }
    }

    return fallbackMessage;
};

// E04: запрос, отменённый нашим же AbortController (уход со страницы/unmount),
// — не ошибка. Пробрасываем как есть, без console.error и без переупаковки,
// чтобы вызывающий код мог отличить отмену по error.code === 'ERR_CANCELED'.
const isAbortError = (error) =>
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

// E01 — массивы в multipart. `FormData.append(key, ['a', 'b'])` приводит массив
// к строке `"a,b"`, и бэкенд, читающий поле через `getlist`, видит одно
// невалидное значение (мультикатегории события падали с 400). Кладём массив
// поэлементно — так multipart несёт настоящее multi-value поле.
export const appendFormValue = (formData, key, value) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
        value.forEach((item) => {
            if (item !== undefined && item !== null) {
                formData.append(key, item);
            }
        });
        return;
    }
    formData.append(key, value);
};

const handleApiError = (error, customMessage) => {
    if (isAbortError(error)) {
        throw error;
    }
    const errorMessage = customMessage || 'API request failed';
    console.error(`${errorMessage}:`, error.response?.data || error.message);
    // E03 — `customMessage` это техническая метка для логов, часто английская
    // («Failed to create event»). В UI она попадать не должна: сообщение для
    // пользователя берём из ответа бэка, иначе — русский дефолт
    // `extractApiErrorMessage`.
    const wrapped = new Error(extractApiErrorMessage(error));
    // Preserve the axios `response` so callers' catch-blocks can still inspect
    // `err.response.data` for DRF field errors (e.g. {"costs": ["..."]}).
    // Without this the modal sees only `wrapped.message` and falls through
    // to the generic "Не удалось сохранить" fallback (#consult-modal bug).
    if (error?.response) wrapped.response = error.response;
    if (error?.config) wrapped.config = error.config;
    if (error?.isAxiosError) wrapped.isAxiosError = true;
    throw wrapped;
};

// ===============================
// 📌 AUTH (inline / tensionless booking)
// ===============================

/**
 * Tensionless minimal-registration helper used by BookingModal Step 0.
 * Posts only ``{email, minimal: true}`` to ``/api/auth/register/`` — the
 * backend (T4) accepts the email-only payload, queues an OTP email, and
 * leaves first_name/last_name/phone empty until the user completes their
 * profile later. The flag is purely additive; if T4 is not deployed yet
 * the same payload still succeeds because email was already the only
 * required field on RegisterSerializer (``minimal`` is silently ignored
 * by older serializers). Callers handle the ``email_in_use`` 400 path
 * themselves (see BookingModal — it switches to the login-passcode flow).
 */
export const registerMinimalApi = (email) => api.post('/auth/register/', { email, minimal: true });

// ===============================
// 📌 PAYMENTS
// ===============================

export const fetchPaymentTokensApi = async () => {
    try {
        const response = await api.get('/payments/tokens/');
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch payment tokens');
    }
};

export const savePaymentTokenApi = async (payload) => {
    try {
        const response = await api.post('/payments/tokens/', payload);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to save payment token');
    }
};

export const deletePaymentTokenApi = async (tokenOrMethodId) => {
    try {
        validateId(tokenOrMethodId);
        await api.delete(`/payments/tokens/${tokenOrMethodId}/`);
    } catch (error) {
        handleApiError(error, 'Failed to delete payment token');
    }
};

// E04: опциональный signal — вызывающая сторона (mount-burst CreateEntityPage)
// привязывает его к unmount, чтобы отменить ещё не завершённый запрос.
export const fetchPaymentProviderConfigApi = async (signal) => {
    try {
        const response = await api.get('/payments/tokens/provider-config/', {
            suppressErrorToast: true,
            signal,
        });
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch payment provider configuration');
    }
};

/**
 * Запустить привязку карты для выплат.
 *
 * @param {string} returnUrl адрес, куда вернуть человека после формы банка
 * @param {string} [replacesPayoutMethodId] public_id заменяемой карты — для
 *   флоу «Изменить карту». Замену доводит сервер: пока намерение жило только
 *   в sessionStorage, активация привязки вебхуком (пока человек был на
 *   странице банка) оставляла старую карту основной, и выплата уходила на неё.
 */
export const startTbankCardBindingApi = async (returnUrl, replacesPayoutMethodId) => {
    try {
        const response = await api.post('/payments/tokens/tbank/card-binding/start/', {
            return_url: returnUrl,
            ...(replacesPayoutMethodId
                ? { replaces_payout_method_id: replacesPayoutMethodId }
                : {}),
        });
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to start T-Bank card binding');
    }
};

export const fetchPaymentDealApi = async (dealId) => {
    try {
        validateId(dealId);
        const response = await api.get(`/payments/deals/${dealId}/`);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch payment deal');
    }
};

export const removeTbankPayoutMethodApi = async (payoutMethodId) => {
    try {
        validateId(payoutMethodId);
        await api.delete(`/payments/tokens/tbank/methods/${payoutMethodId}/`);
    } catch (error) {
        handleApiError(error, 'Failed to remove T-Bank payout method');
    }
};

export const refreshTbankCardBindingApi = async (payoutMethodId) => {
    try {
        validateId(payoutMethodId);
        const response = await api.post('/payments/tokens/tbank/card-binding/refresh/', {
            payout_method_id: payoutMethodId,
        });
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to refresh T-Bank card binding');
    }
};

// ===============================
// 📌 EVENT REGISTRATIONS
// ===============================

// ``suppressErrorToast`` — для фоновой догрузки поверх уже открытого экрана
// (попап деталей групповой сессии): провал списка записавшихся он сознательно
// НЕ считает ошибкой — стопка аватаров приезжает из GetEvent, — и красный тост
// сообщал бы о неудаче, которой для посетителя не было. Страница события флаг
// не передаёт: там тост остаётся её единственным сигналом.
export const getEventRegistrationsApi = async (eventId, { suppressErrorToast = false } = {}) => {
    try {
        validateId(eventId);
        const response = await api.get('/events/get_event_registrations/', {
            params: { event_id: eventId },
            suppressErrorToast,
        });
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch registrations for event with ID: ${eventId}`);
    }
};

export const checkRegistrationApi = async (eventId, userId) => {
    try {
        validateId(eventId);
        const params = { event_id: eventId };
        if (userId || userId === 0) {
            params.user_id = userId;
        }
        const response = await api.get('/events/check_registration/', { params });
        return response.data.is_registered;
    } catch (error) {
        handleApiError(error, `Failed to check registration for event: ${eventId}`);
        return false;
    }
};

export const registerForEventApi = async ({ eventId, userId, menteeQuestion = '' }) => {
    try {
        validateId(eventId);
        const payload = {
            event_id: eventId,
        };
        if (userId || userId === 0) {
            payload.user_id = userId;
        }
        if (menteeQuestion) {
            payload.mentee_question = menteeQuestion;
        }
        const response = await api.post('/events/register_for_event/', payload);
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to register for event: ${eventId}`);
    }
};

export const unregisterFromEventApi = async (eventId, userId) => {
    try {
        validateId(eventId);
        const payload = { event_id: eventId };
        if (userId || userId === 0) {
            payload.user_id = userId;
        }
        const response = await api.post('/events/unregister/', payload);
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to unregister from event: ${eventId}`);
    }
};

// ===============================
// 📌 Mentors (Сессии)
// ===============================

export const getWorkExperiencesApi = async (userId) =>
    api.get('/work-experiences/', { params: { user: userId } }).then((r) => r.data);

export const getEducationsApi = async (userId) =>
    api.get('/educations/', { params: { user: userId } }).then((r) => r.data);

// ===============================
// 📌 EVENTS (Сессии)
// ===============================

// export const getEventsApi = async () => {
//     try {
//         const res = await api.get('/events/GetEvents/');
//         return res.data;
//     } catch (error) {
//         handleApiError(error, 'Failed to fetch events');
//     }
// };

export const getUserEventsAPI = async (userId, signal) => {
    try {
        validateUserId(userId);
        const res = await api.get('/events/GetUserEvents/', {
            params: { user_id: userId },
            signal,
        });
        return res.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch user events');
    }
};

// ``suppressErrorToast`` — см. комментарий у getEventRegistrationsApi. Попап
// деталей рисует провал сам («Полное описание не загрузилось» + путь на
// страницу сессии), и дублирующий тост поверх открытого попапа только мешает.
export const getEventByIdApi = async (id, { suppressErrorToast = false } = {}) => {
    try {
        validateId(id);
        const res = await api.get('/events/GetEvent/', {
            params: { id },
            suppressErrorToast,
        });
        return res.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch event with ID: ${id}`);
    }
};

export const getTopEventsApi = async () => {
    try {
        const res = await api.get('/events/GetTopEvents');
        return res.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch top events');
    }
};

// export const loadMoreEventsApi = async (offset = 0, limit = 10) => {
//     try {
//         if (offset < 0) offset = 0;
//         if (limit <= 0) limit = 10;

//         const res = await api.get('/events/load_more/', {
//             params: { offset, limit }
//         });
//         return res.data;
//     } catch (error) {
//         handleApiError(error, 'Failed to load more events');
//     }
// };

// Функция для преобразования объекта параметров в строку запроса
function objectToQueryString(obj) {
    const params = [];

    for (const key in obj) {
        if (Array.isArray(obj[key])) {
            // Если значение - массив, добавляем каждый элемент как отдельный параметр
            obj[key].forEach((value) => {
                params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
            });
        } else {
            params.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`);
        }
    }

    return params.join('&');
}

// Функция для загрузки событий с фильтрацией
export async function loadMoreEventsApi(params) {
    try {
        const queryString = objectToQueryString(params);
        console.log('API request with params:', queryString); // Добавляем логирование для отладки
        const response = await api.get(`/events/load_more/?${queryString}`);

        const data = response.data;
        console.log('API response:', data); // Добавляем логирование для отладки
        return data;
    } catch (error) {
        console.error('Error loading events:', error);
        throw error;
    }
}

// Функция для получения всех событий
export async function getEventsApi() {
    try {
        const response = await api.get('/events/GetEvents/');

        return response.data;
    } catch (error) {
        console.error('Error getting events:', error);
        throw error;
    }
}

export const getPaginatedEventsApi = async (page = 1) => {
    try {
        const res = await api.get(`/events/paginated_events/`, {
            params: { page },
        });
        return res.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch paginated events for page ${page}`);
    }
};

export const createEventApi = async (eventData, imageFile = null) => {
    try {
        if (!eventData) {
            throw new Error('Event data is required');
        }

        await ensureFreshToken();

        const formData = new FormData();

        // Validate required fields
        const requiredFields = ['title', 'date', 'mentor_id'];
        for (const field of requiredFields) {
            if (!eventData[field]) {
                throw new Error(`${field} is required`);
            }
        }

        // Add all event data to formData
        Object.entries(eventData).forEach(([key, value]) => {
            appendFormValue(formData, key, value);
        });

        // Add image file if provided
        if (imageFile) {
            formData.append('event_image', imageFile);
            console.log('Adding image file to form data:', imageFile.name, imageFile.size);
        }

        console.log('Sending form data to API:', Object.fromEntries(formData.entries()));

        const res = await api.post('/events/AddEvent/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return res.data;
    } catch (error) {
        console.error('Create event error details:', error.response?.data);
        handleApiError(error, 'Failed to create event');
    }
};

export const updateEventApi = async (id, eventData, imageFile = null) => {
    try {
        validateId(id);

        if (!eventData) {
            throw new Error('Event data is required');
        }

        await ensureFreshToken();

        // Если есть файл изображения или флаг удаления, используем FormData
        if (imageFile || eventData.remove_image) {
            const formData = new FormData();

            // Добавляем все поля в FormData
            Object.entries(eventData).forEach(([key, value]) => {
                appendFormValue(formData, key, value);
            });

            // Добавляем файл изображения, если он есть
            if (imageFile) {
                formData.append('event_image', imageFile);
                console.log(
                    'Adding image file to form data for update:',
                    imageFile.name,
                    imageFile.size,
                );
            }

            console.log(
                'Sending form data to API for update:',
                Object.fromEntries(formData.entries()),
            );

            const res = await api.put(`/events/UpdateEvent/`, formData, {
                params: { id },
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return res.data;
        } else {
            // Обычный JSON запрос, если нет изменений с изображением
            const res = await api.put(`/events/UpdateEvent/`, eventData, {
                params: { id },
            });
            return res.data;
        }
    } catch (error) {
        console.error('Update event error details:', error.response?.data);
        handleApiError(error, `Failed to update event with ID: ${id}`);
    }
};

// export const createEventApi = async (eventData, imageFile = null) => {
//     try {
//         if (!eventData) {
//             throw new Error('Event data is required');
//         }

//         const formData = new FormData();

//         // Validate required fields
//         const requiredFields = ['title', 'date'];
//         for (const field of requiredFields) {
//             if (!eventData[field]) {
//                 throw new Error(`${field} is required`);
//             }
//         }

//         Object.entries(eventData).forEach(([key, value]) => {
//             if (value !== undefined && value !== null) {
//                 formData.append(key, value);
//             }
//         });

//         if (imageFile) {
//             formData.append('event_image', imageFile);
//         }

//         const res = await api.post('/events/AddEvent/', formData, {
//             headers: {
//                 'Content-Type': 'multipart/form-data',
//             },
//         });

//         return res.data;
//     } catch (error) {
//         handleApiError(error, 'Failed to create event');
//     }
// };

// export const updateEventApi = async (id, eventData) => {
//     try {
//         validateId(id);

//         if (!eventData) {
//             throw new Error('Event data is required');
//         }

//         const res = await api.put(`/events/UpdateEvent`, eventData, {
//             params: { id },
//         });
//         return res.data;
//     } catch (error) {
//         handleApiError(error, `Failed to update event with ID: ${id}`);
//     }
// };

export const deleteEventApi = async (id) => {
    try {
        validateId(id);

        const res = await api.delete('/events/DeleteEvent/', {
            params: { id },
        });
        return res.data;
    } catch (error) {
        handleApiError(error, `Failed to delete event with ID: ${id}`);
    }
};

// ``suppressErrorToast`` — для публичной витрины (MentorPage / ClaimMentorPage):
// там сбой чтения услуг деградирует в пустой блок, который страница рисует
// сама, и красная плашка поверх маркетинговой страницы только мешает (N2).
// Кабинет наставника флаг не передаёт — глобальный тост остаётся его
// единственным сигналом об ошибке.
// ``signal`` — последним параметром, как у остальных читающих функций (E04).
export const fetchConsultationTypesApi = async (
    userId,
    { suppressErrorToast = false } = {},
    signal,
) => {
    try {
        const response = await api.get('/consultation-types/all/', {
            params: { user_id: userId },
            suppressErrorToast,
            signal,
        });
        return response.data;
    } catch (error) {
        if (isAbortError(error)) throw error; // E04 — отмена, не ошибка
        console.error('Error fetching consultation types:', error.response?.data || error.message);
        throw new Error('Failed to fetch consultation types');
    }
};

// Both helpers accept an optional ``{ config }`` second argument that is
// forwarded to axios. The ConsultationModal renders inline field-level
// errors via extractFieldErrors and therefore passes
// ``{ suppressErrorToast: true }`` to keep the global interceptor quiet.
export const createConsultationTypeApi = async (data, { config = {} } = {}) => {
    try {
        await ensureFreshToken();
        const response = await api.post('/consultation-types/create/', data, config);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Не удалось создать тип личной сессии');
    }
};

export const updateConsultationTypeApi = async (data, { config = {} } = {}) => {
    try {
        await ensureFreshToken();
        const userId = typeof data.user === 'object' && data.user.id ? data.user.id : data.user;
        const response = await api.patch(
            `/consultation-types/${data.id}/update/`,
            { ...data, user_id: userId },
            config,
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Не удалось обновить тип личной сессии');
    }
};

export const deleteConsultationTypeApi = async (id, userId) => {
    try {
        await api.delete(`/consultation-types/${id}/delete/`, {
            params: { user_id: userId },
        });
        return true;
    } catch (error) {
        handleApiError(error, 'Не удалось удалить тип личной сессии');
    }
};

// ===============================
// 📌 CONSULTATION SLOTS
// ===============================

export const fetchSlotsApi = async (
    userId,
    { includeUnbookable = false, suppressErrorToast = false } = {},
    signal,
) => {
    // Sprint #27 / audit 2026-05-26: backend filter on /all/ теперь по
    // умолчанию возвращает только bookable-слоты (notice-окно, booked,
    // cancelled_by_mentor отсекаются). Mentor's-own-page (CreateEntityPage,
    // recurrence table) должен передавать includeUnbookable=true, чтобы
    // увидеть ВСЕ слоты — иначе UI пропадёт. Public profile (MentorPage,
    // ClaimMentorPage) использует default, иначе менти кликает на слот
    // в окне lead-time и получает 409 ``slot_starts_too_soon``.
    const params = { user_id: userId };
    if (includeUnbookable) {
        params.include_unbookable = 'true';
    }
    // ``suppressErrorToast`` — см. комментарий у fetchConsultationTypesApi (N2).
    const response = await api.get('/consultation-type-slots/all/', {
        params,
        suppressErrorToast,
        signal,
    });
    return response.data;
};

export const createSlotApi = async (data) => {
    await ensureFreshToken();
    const response = await api.post('/consultation-type-slots/create/', data);
    return response.data;
};

export const deleteSlotApi = async (id, userId) => {
    await api.delete(`/consultation-type-slots/${id}/delete/`, {
        params: { user_id: userId },
    });
    return true;
};

export const deleteSeriesApi = async (slotId, userId) => {
    const response = await api.delete(`/consultation-type-slots/${slotId}/delete_series/`, {
        params: { user_id: userId },
    });
    return response.data;
};

export const updateSlotApi = async (slotId, data) => {
    await ensureFreshToken();
    const response = await api.patch(`/consultation-type-slots/${slotId}/update/`, data);
    return response.data;
};

export const createRecurrentSlotsApi = async (data) => {
    await ensureFreshToken();
    const response = await api.post('/consultation-type-slots/create_recurrent_slots/', data);
    return response.data;
};

export const getAvailableConsultationSlotsApi = async ({
    consultationTypeId,
    startDate,
    endDate,
}) => {
    try {
        validateId(consultationTypeId);
        const response = await api.get('/consultation-type-slots/available/', {
            params: {
                consultation_type_id: consultationTypeId,
                start_date: startDate,
                end_date: endDate,
            },
        });
        return response.data;
    } catch (error) {
        handleApiError(
            error,
            `Failed to fetch available consultation slots for consultationTypeId=${consultationTypeId}`,
        );
    }
};

export const getConsultationSlotDetailApi = async (slotId) => {
    try {
        validateId(slotId);
        const response = await api.get(`/consultation-type-slots/${slotId}/detailed_info/`);
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch slot details for slotId=${slotId}`);
    }
};

export const createConsultationBookingApi = async (payload) => {
    try {
        await ensureFreshToken();
        const response = await api.post('/consultation-bookings/create/', payload);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to create consultation booking');
    }
};

/**
 * Reports that one party of a consultation booking did not show up.
 * Self-service endpoint (E2 Stage 2). Server propagates the flag to
 * Conference.status so that the auto-release pipeline (E2 Stage 1)
 * defers the mentor's payout until the situation is resolved.
 *
 * @param {string} bookingPublicId — UUID public_id of the booking
 * @param {'mentor' | 'mentee'} party — absent party being reported
 */
export const reportConsultationNoShowApi = async (bookingPublicId, party) => {
    validateId(bookingPublicId);
    if (party !== 'mentor' && party !== 'mentee') {
        throw new Error('party must be "mentor" or "mentee"');
    }
    try {
        await ensureFreshToken();
        const response = await api.post(
            `/consultation-bookings/${bookingPublicId}/report-no-show/`,
            { party },
        );
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to report no-show');
    }
};

/**
 * «Постучаться» в отсутствующего участника сессии (#38).
 *
 * Делает ``POST /api/consultation-bookings/<public_id>/knock/``. Сервер сам
 * определяет, кто пришёл (== ``request.user``) и кому слать стук
 * (counterparty), а также применяет rate-limit 1/5min.
 *
 * MVP-каналы: email + платформа. SMS-канал отложен до интеграции
 * SMS-шлюза (см. backend TODO #38).
 *
 * @param {string} bookingPublicId — UUID public_id of the booking
 * @returns {Promise<{status: 'sent', absent_party: 'mentor' | 'mentee'}>}
 */
export const knockAbsentParticipantApi = async (bookingPublicId) => {
    validateId(bookingPublicId);
    try {
        await ensureFreshToken();
        const response = await api.post(`/consultation-bookings/${bookingPublicId}/knock/`, {});
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to knock absent participant');
    }
};

export const createConsultationHoldApi = async (slotId) => {
    validateId(slotId);
    try {
        await ensureFreshToken();
        // BookingModal owns the full 409/429/410/auth error mapping (via
        // mapBookingHoldError + emitToast) and renders an inline error label
        // (`holdError`) inside the modal. Without suppressing the global
        // httpClient interceptor, a 4xx response would surface two toasts —
        // the raw DRF `detail` from the interceptor AND the mapped copy from
        // the modal — which is what the user reported. The local catch is the
        // single source of truth here.
        //
        // RES-Ф1: computed-слоты Calendly-окон приходят с синтетическим id
        // `win:<ct_public_id>:<unix_ts>` (BE availability_compute). Detail-роут
        // ждёт UUID → для win:-id это 404. Парсим win-id и зовём коллекционный
        // endpoint POST /consultation-type-slots/hold/ с
        // consultation_type_id + start_time (ISO). UUID-слоты — как раньше.
        if (typeof slotId === 'string' && slotId.startsWith('win:')) {
            const [, consultationTypeId, unixTs] = slotId.split(':');
            const response = await api.post(
                '/consultation-type-slots/hold/',
                {
                    consultation_type_id: consultationTypeId,
                    start_time: new Date(Number(unixTs) * 1000).toISOString(),
                },
                { suppressErrorToast: true },
            );
            return response.data;
        }
        const response = await api.post(`/consultation-type-slots/${slotId}/hold/`, undefined, {
            suppressErrorToast: true,
        });
        return response.data;
    } catch (error) {
        console.error('Failed to create consultation hold:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Отменяет неоплаченное удержание (hold) консультационного слота.
 * Делает DELETE /api/consultation-slot-holds/<holdId>/ — после успеха
 * слот возвращается в свободные, а связанные платёжные сущности завершаются
 * фоновой задачей.
 */
export const cancelConsultationHoldApi = async (holdId) => {
    validateId(holdId);
    try {
        await ensureFreshToken();
        await api.delete(`/consultation-slot-holds/${holdId}/`);
        return true;
    } catch (error) {
        console.error('Failed to cancel consultation hold:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Возвращает до 20 последних просроченных броней авторизованного менти
 * (E4 Stage 1, codex ffccc7c). Каждый элемент содержит `resume`-подсказку:
 *  - "same_slot_available" — исходный слот всё ещё свободен, можно повторить
 *    оплату того же времени;
 *  - "slot_taken_alternatives" — слот занят, надо выбрать другое время.
 *
 * Возвращает пустой массив на любых ошибках — баннер «Не оплачено» не должен
 * ломать главную/календарь при недоступной сети.
 */
export const fetchExpiredHoldsApi = async () => {
    try {
        const response = await api.get('/consultation-slot-holds/expired/');
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Failed to fetch expired holds:', error.response?.data || error.message);
        return [];
    }
};

export const getConsultationTypeDetailApi = async (typeId, userId) => {
    try {
        validateId(typeId);
        validateUserId(userId);
        const response = await api.get(`/consultation-types/${typeId}/detail/`, {
            params: { user_id: userId },
        });
        return response.data;
    } catch (error) {
        handleApiError(
            error,
            `Failed to fetch consultation type detail for typeId=${typeId}, userId=${userId}`,
        );
    }
};

// ===============================
// 📌 REVIEWS
// ===============================

export const fetchMentorReviewsApi = async (mentorId) => {
    try {
        const response = await api.get('/user_reviews/', {
            params: { mentor: mentorId },
        });
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch mentor reviews for mentorId=${mentorId}`);
    }
};

export const createMentorReviewApi = async ({ mentorId, reviewText, reviewStars }) => {
    try {
        const response = await api.post('/user_reviews/', {
            mentor_id: mentorId,
            review_text: reviewText,
            review_stars: reviewStars,
        });
        return response.data;
    } catch (error) {
        const message = extractApiErrorMessage(
            error,
            'Не удалось отправить отзыв. Попробуйте позже.',
        );
        console.error('Failed to create mentor review:', error.response?.data || error.message);
        throw new Error(message);
    }
};

export const fetchEventReviewsApi = async (eventId) => {
    try {
        const response = await api.get('/event_reviews/', {
            params: { event: eventId },
        });
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch event reviews for eventId=${eventId}`);
    }
};

export const createEventReviewApi = async ({ eventId, reviewText, reviewStars }) => {
    try {
        const response = await api.post('/event_reviews/', {
            event_id: eventId,
            review_text: reviewText,
            review_stars: reviewStars,
        });
        return response.data;
    } catch (error) {
        const message = extractApiErrorMessage(
            error,
            'Не удалось отправить отзыв о событии. Попробуйте позже.',
        );
        console.error('Failed to create event review:', error.response?.data || error.message);
        throw new Error(message);
    }
};

// ===============================
// 📌 BOOKINGS
// ===============================

export const fetchBookingsApi = async (userId) => {
    try {
        await ensureFreshToken();
        const response = await api.get('/consultation-bookings/all', {
            params: { user_id: userId },
        });
        return response.data;
    } catch (error) {
        handleApiError(error, 'Не удалось загрузить бронирования');
    }
};

export const fetchConsultationBookingsByMentorApi = async (mentorId) => {
    try {
        validateUserId(mentorId);
        const response = await api.get('/consultation-bookings/list_by_mentor/', {
            params: { mentor_id: mentorId },
        });
        return response.data;
    } catch (error) {
        handleApiError(error, `Failed to fetch consultation bookings for mentor: ${mentorId}`);
    }
};

/**
 * Mentor-side feed for the «Кто записан» card on a consultation page —
 * scoped to a single ConsultationType. Returns
 *   { bookings: [...], registered_count: number }
 * with booker profile (id/name/avatar) and `mentee_question`.
 * Only the owner of the consultation type (or staff) gets 200; everyone
 * else receives 403 — handled by the caller (we just return null on error).
 */
export const fetchConsultationBookingsByConsultationTypeApi = async (consultationTypeId) => {
    try {
        validateId(consultationTypeId);
        const response = await api.get('/consultation-bookings/list_by_consultation_type/', {
            params: { consultation_type_id: consultationTypeId },
        });
        return response.data;
    } catch (error) {
        handleApiError(
            error,
            `Failed to fetch bookings for consultation type: ${consultationTypeId}`,
        );
        return null;
    }
};

// =========================
// 📌 PAYMENTS
// =========================

/**
 * Создать safe‑deal на бэкенде.
 * @param {Object} dto {seller_id, amount, payout_amount, order_id}
 * @return {Promise<{deal_id:string}>}
 */
export const createDealApi = (dto) => api.post('/payments/deals/', dto).then((r) => r.data);

/**
 * Создать платеж внутри сделки.
 * @param {string} dealId
 * @param {string} returnUrl
 * @return {Promise<{payment_id, confirmation_url}>}
 */
export const createPaymentApi = (dealId, returnUrl) =>
    api.post(`/payments/deals/${dealId}/payments/`, { return_url: returnUrl }).then((r) => r.data);

// Used only by PaymentModal, which renders its own inline state.error via
// processError(). Opt out of the global axios error-toast interceptor so
// 410/429/etc. don't produce a duplicate yellow toast next to the modal's
// red banner. If a future caller wants the global toast back they can
// either drop this flag or wrap their call without `suppressErrorToast`.
export const fetchPaymentLinkApi = (dealId) =>
    api
        .get(`/payments/deals/${dealId}/payment-link/`, { suppressErrorToast: true })
        .then((r) => r.data);

// P3-edge: create-or-resume order-root сделку для существующей НЕоплаченной
// брони. Календарь шлёт ``deal_id = order.public_id`` (``order_…``) без holdId,
// и если SecureDeal по заказу ещё не создан, resume (fetchPaymentLinkApi) даёт
// 404. Тогда дёргаем этот эндпоинт с тем же ``order_…`` — BE до-создаёт сделку
// на существующий Order (слот держит Reservation) и возвращает confirmation_url.
// Идемпотентно на стороне BE. suppressErrorToast: вызывающий сам решает UX.
export const ensureOrderPaymentApi = (orderId, returnUrl) =>
    api
        .post(
            '/payments/deals/ensure-payment/',
            { order_id: orderId, ...(returnUrl ? { return_url: returnUrl } : {}) },
            { suppressErrorToast: true },
        )
        .then((r) => r.data);

/**
 * Получить сохранённые payout‑токены текущего пользователя.
 */
export const fetchTokensApi = () => api.get('/payments/tokens/').then((r) => r.data);

/**
 * Сохранить новый payout‑токен (из виджета YooKassa).
 */
export const saveTokenApi = (dto) => api.post('/payments/tokens/', dto).then((r) => r.data);
/**
 * Запросить выплату продавцу (payout) — деньги уйдут после успешного
 * статуса у YooKassa (ждём webhook).
 */
export const createPayoutApi = (dealId, body = {}) =>
    api.post(`/payments/deals/${dealId}/payout/`, body).then((r) => r.data);

// =========================
// 📌 Mentor Application
// =========================

export const getMyMentorApplicationApi = async () => {
    // suppressErrorToast — 404 здесь означает «у user'а ещё нет заявки»,
    // ожидаемое состояние; toast в header'е на mount'е был бы дезориентирующим.
    const res = await api.get('/mentor-applications/me/', { suppressErrorToast: true });
    return res.data; // 404 → «нет черновика»
};

export const createMentorApplicationApi = async (payload) => {
    const res = await api.post('/mentor-applications/', { payload }, { suppressErrorToast: true });
    return res.data; // {id, status, …}
};

export const saveMentorApplicationDraftApi = async (id, payload) => {
    const res = await api.patch(
        `/mentor-applications/${id}/`,
        { payload },
        { suppressErrorToast: true },
    );
    return res.data;
};

export const submitMentorApplicationApi = async (id) => {
    const res = await api.post(`/mentor-applications/${id}/submit/`, undefined, {
        suppressErrorToast: true,
    });
    return res.data; // {status: "submitted"}
};

export const cancelMentorApplicationApi = async (id) => {
    const res = await api.post(`/mentor-applications/${id}/cancel/`);
    return res.data;
};

// =========================
// 📌 NOTIFICATIONS
// =========================

export const fetchNotificationsApi = async (params = {}, signal) => {
    const res = await api.get('/notifications/', { params, signal });
    return res.data; // {results, count, next, …}
};

export const fetchNotificationsUnreadCountApi = async (signal) => {
    // D-9: точный счётчик непрочитанных, не зависящий от пагинации.
    // suppressErrorToast — для анонима 401/404 нормально, не нужен toast.
    // E04 — signal привязан к жизненному циклу колокола (NotificationComponent):
    // poll-цикл отменяется при unmount/логауте.
    const res = await api.get('/notifications/unread_count/', { suppressErrorToast: true, signal });
    return res.data; // {count: number}
};

// --- v2: action/info inbox -----------------------------------------------
export const markNotificationsViewedApi = async (ids) => {
    const res = await api.post('/notifications/mark_viewed/', { ids });
    return res.data;
};

export const actOnNotificationApi = async (publicId) => {
    const res = await api.post(`/notifications/${publicId}/act/`);
    return res.data;
};

export const dismissNotificationApi = async (publicId) => {
    const res = await api.post(`/notifications/${publicId}/dismiss/`);
    return res.data;
};

export const bulkDismissNotificationsApi = async (params) => {
    // params: { ids?: string[], filter?: { kind?: 'action'|'info', status?: 'pending'|'viewed' } }
    const res = await api.post('/notifications/bulk_dismiss/', params);
    return res.data;
};

export const fetchNotificationsByTabApi = async (tab, category, q) => {
    const params = { tab };
    if (category) params.category = category;
    if (q) params.q = q;
    const res = await api.get('/notifications/', { params });
    return res.data;
};

// -------------------------
// 📌 WORK EXPERIENCE
// -------------------------
export const fetchWorkExperiences = () => api.get('/work-experiences/').then((r) => r.data);

export const createWorkExperience = (dto) =>
    api.post('/work-experiences/', dto).then((r) => r.data);

export const updateWorkExperience = (id, dto) =>
    api.patch(`/work-experiences/${id}/`, dto).then((r) => r.data);

export const deleteWorkExperience = (id) =>
    api.delete(`/work-experiences/${id}/`).then((r) => r.data);

// -------------------------
// 📌 EDUCATION
// -------------------------
export const fetchEducations = () => api.get('/educations/').then((r) => r.data);

export const createEducation = (dto) => api.post('/educations/', dto).then((r) => r.data);

export const updateEducation = (id, dto) =>
    api.patch(`/educations/${id}/`, dto).then((r) => r.data);

export const deleteEducation = (id) => api.delete(`/educations/${id}/`).then((r) => r.data);

// -------------------------
// GET /api/payments/payout-methods/me/ — current user's payout methods.
// Backend filters REMOVED-tombstones by default.
// Returns an empty array (NOT 404) for users without methods yet.
// NB: продовых потребителей у обёртки сейчас нет. Дашборд-баннеры и чип
// гейтятся ступенью `selling_readiness` из стора, а страница выплат читает
// свой /payments/config/. Прежний потребитель — composable usePayoutStatus —
// удалён вместе с баннером, который его звал.
// -------------------------
export const fetchMyPayoutMethodsApi = async () => {
    const response = await api.get('/payments/payout-methods/me/');
    return response.data;
};

// -------------------------
// PATCH /api/users/me/ — partial update of UserProfile fields.
// Used by the OnboardingPublishStep to flip `is_profile_public=true`
// on finalize, and is the right endpoint for any future single-field
// profile toggles. Backend serializer validates per-field (e.g.
// is_profile_public is bool; cover_url is nullable). Caller passes only
// the fields it wants changed.
// -------------------------
export const patchMyProfileApi = async (partial) => {
    const response = await api.patch('/users/me/', partial);
    return response.data;
};

// -------------------------
// Personal slugs / public profiles
// -------------------------
export const checkMentorSlugApi = async (slug) => {
    const response = await api.get('/users/slug/check/', { params: { slug } });
    return response.data;
};

export const updateMentorSlugApi = async (slug) => {
    const response = await api.post('/users/me/slug/', { slug });
    return response.data;
};

export const ensureMentorSlugApi = async (options = {}) => {
    const response = await api.post('/users/me/slug/auto/', options);
    return response.data;
};

// #43 — unified social-links editor (mentor + menti).
// GET returns ``{social_links: [{network, url, order}]}``; PATCH atomically
// replaces the full list (server caps the array at 8 entries).
export const fetchMySocialLinksApi = async () => {
    const response = await api.get('/users/me/social-links/');
    return response.data;
};

export const updateMySocialLinksApi = async (socialLinks) => {
    const response = await api.patch('/users/me/social-links/', {
        social_links: socialLinks,
    });
    return response.data;
};

// -------------------------
// Mentor tags
// -------------------------
// Free-form topic labels a mentor self-applies. Backend contract:
//   GET  /api/mentor-tags/?q=<prefix>     → {results: [{name, slug, usage_count}]}
//   GET  /api/mentor-tags/popular/?limit= → [{name, slug, usage_count}]
//   GET  /api/users/me/tags/              → {tags: [{name, slug, usage_count}]}
//   PUT  /api/users/me/tags/              → body {tags: ["A", "B"]}, max 10.
// The PUT auto-creates missing tags case-insensitively. Catalog filters via
// `?tag=<slug>` (single) or `?tags=slug1,slug2` (intersection).
export const searchMentorTagsApi = async (query = '') => {
    const params = query ? { q: query } : {};
    const response = await api.get('/mentor-tags/', { params });
    // Paginated response when q is set, plain list when no pagination kicks in.
    const data = response.data;
    if (Array.isArray(data)) {
        return data;
    }
    return Array.isArray(data?.results) ? data.results : [];
};

export const fetchPopularMentorTagsApi = async (limit = 20) => {
    const response = await api.get('/mentor-tags/popular/', {
        params: { limit },
    });
    return Array.isArray(response.data) ? response.data : [];
};

export const fetchMyMentorTagsApi = async () => {
    const response = await api.get('/users/me/tags/');
    return Array.isArray(response.data?.tags) ? response.data.tags : [];
};

export const updateMyMentorTagsApi = async (tagNames) => {
    const response = await api.put('/users/me/tags/', { tags: tagNames });
    return Array.isArray(response.data?.tags) ? response.data.tags : [];
};

// -------------------------
// Profile cover image
// -------------------------
// Mentors (and other users) can upload a custom cover for their public
// profile. The avatar field is still saved through the main PATCH /users/me/
// form; the cover lives on a dedicated endpoint so we can refresh just the
// header without round-tripping the full profile form.
//
// Backend contract:
//   POST   /users/me/cover/  multipart {cover: File}  → {cover_url}
//   DELETE /users/me/cover/                           → 204 (cover_url=null)
export const uploadCoverApi = (file) => {
    const formData = new FormData();
    formData.append('cover', file);
    return api.post('/users/me/cover/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
};

export const deleteCoverApi = () => api.delete('/users/me/cover/');

export const fetchPublicMentorBySlugApi = async (slug) => {
    const response = await api.get(`/public/mentors/by-slug/${slug}/`);
    return response.data;
};

// E50: резолв канонического адреса профиля по public_id. Нужен гарду роута
// /mentor_info/:mentorId, который уводит на /<slug>, когда слаг у наставника
// есть. suppressErrorToast — 404 тут штатный ответ (профиля нет), гард сам
// решает, что делать, и тост поверх был бы шумом.
export const fetchPublicUserByIdApi = async (userId) => {
    const response = await api.get(`/users/${userId}/`, { suppressErrorToast: true });
    return response.data;
};

export const trackPublicMentorViewApi = async (slug, payload = {}) => {
    const response = await api.post(`/public/mentors/${slug}/track-view/`, payload);
    return response.data;
};

// -------------------------
// Mentor claim public flow
// -------------------------
const normalizeClaimToken = (token) => {
    const normalized = (token ?? '').toString().trim();
    if (!normalized) {
        throw new Error('Claim token is required');
    }
    return normalized;
};

// suppressErrorToast на всех трёх запросах клейма: ClaimMentorPage рисует любую
// ошибку сама — экраном «Ссылка недоступна» либо inline-плашкой в доке. Без
// флага глобальный интерцептор httpClient добавляет тост с тем же `detail`, и
// пользователь видит одно сообщение дважды (E23). Правило описано в
// httpClient.ts у типа RetryableRequestConfig.
export const getMentorClaimPreviewApi = async (token) => {
    const normalizedToken = normalizeClaimToken(token);
    const response = await api.get('/mentor-claims/preview/', {
        params: { token: normalizedToken },
        suppressErrorToast: true,
    });
    return response.data;
};

export const submitMentorClaimEmailApi = async ({ token, email }) => {
    const normalizedToken = normalizeClaimToken(token);
    const normalizedEmail = (email ?? '').toString().trim().toLowerCase();
    const response = await api.post(
        '/mentor-claims/submit-email/',
        {
            token: normalizedToken,
            email: normalizedEmail,
        },
        { suppressErrorToast: true },
    );
    return response.data;
};

export const verifyMentorClaimOtpApi = async ({ token, email, otp }) => {
    const normalizedToken = normalizeClaimToken(token);
    const normalizedEmail = (email ?? '').toString().trim().toLowerCase();
    const normalizedOtp = (otp ?? '').toString().trim();
    const response = await api.post(
        '/mentor-claims/verify-otp/',
        {
            token: normalizedToken,
            email: normalizedEmail,
            otp: normalizedOtp,
        },
        { suppressErrorToast: true },
    );
    return response.data;
};

export const fetchEngagementApi = async (publicId) => {
    try {
        const response = await api.get(`/engagements/${publicId}/`);
        return response.data;
    } catch (error) {
        handleApiError(error, 'Failed to fetch engagement');
    }
};

// E7 Stage 2 (§15.2): mentor/mentee marks a consultation finished from the
// Session Hub. Backend toggles the per-role *_confirmed flag; once both are
// true the booking transitions to COMPLETED and fulfillment runs.
export const finishConsultationApi = async (bookingPublicId) => {
    const response = await api.post(`/consultation-bookings/${bookingPublicId}/finish/`);
    return response.data;
};

// =========================
// 📌 Availability Windows — Phase 1 of the Calendly-style migration
// =========================
// CRUD over the current mentor's own AvailabilityWindow rows. The BE viewset
// lives at backend/PlatformaAPP/consultations/availability_views.py and is
// gated to mentors via permission_classes — non-mentors get a 403 on writes.
//
// Phase 1 (this commit) wires only the mentor settings UI; menti booking
// still uses the legacy ConsultationTypeSlot pipeline. See the spec doc
// docs/superpowers/specs/2026-05-22-calendly-parity-and-availability-windows-design.md
// §6.3 Phase 1 for the rollout plan. These helpers throw with the original
// axios error on failure so the caller can inspect ``err.response.data`` via
// extractFieldErrors and render per-field validation messages inline (the
// editor uses this for end_time vs start_time, etc.).
const AVAILABILITY_WINDOWS_BASE = '/me/availability-windows/';

/** GET /api/me/availability-windows/ → array of window rows for the caller. */
export const fetchAvailabilityWindowsApi = async () => {
    await ensureFreshToken();
    const response = await api.get(AVAILABILITY_WINDOWS_BASE);
    // DRF ModelViewSet returns either the bare array (no pagination) or
    // ``{results: [...]}`` — accept both so we don't break when pagination
    // is later enabled on this endpoint.
    if (Array.isArray(response.data)) return response.data;
    if (response.data?.results && Array.isArray(response.data.results))
        return response.data.results;
    return [];
};

/** POST /api/me/availability-windows/ — create a single window. */
export const createAvailabilityWindowApi = async (payload) => {
    await ensureFreshToken();
    const response = await api.post(AVAILABILITY_WINDOWS_BASE, payload);
    return response.data;
};

/** PATCH /api/me/availability-windows/<id>/ — partial update. */
export const updateAvailabilityWindowApi = async (id, payload) => {
    validateId(id);
    await ensureFreshToken();
    const response = await api.patch(`${AVAILABILITY_WINDOWS_BASE}${id}/`, payload);
    return response.data;
};

/** DELETE /api/me/availability-windows/<id>/ — remove a window. */
export const deleteAvailabilityWindowApi = async (id) => {
    validateId(id);
    await ensureFreshToken();
    await api.delete(`${AVAILABILITY_WINDOWS_BASE}${id}/`);
    return true;
};

// ВАЖНО: default отдаём ЖИВОЙ ре-привязкой httpClient, а НЕ `export default api`.
// `export default <выражение>` фиксирует СНИМОК значения на момент инициализации
// модуля. Поскольку этот модуль в цикле импортов (httpClient → router → store →
// api.js → httpClient), на момент его догрузки httpClient ещё не присвоил свой
// default → снимок ловил `undefined`, и `import api from '@/services/api'` отдавал
// undefined → `api.post/get` падали с TypeError в meeting-чанке (см. #123/#124).
// `export { default } from` — это ЖИВАЯ привязка: резолвится в актуальный axios
// в момент использования, а не на момент инициализации. Цикл больше не кусается.
export { default } from './httpClient';
